<template>
  <div class="flex" :data-cy="`${field.name}--field`">
    <lf-dropdown
      v-on-click-outside="() => (showFieldTypeDropdown = false)"
      :value="field.type"
      :options="customFieldTypes"
      :toggle-from-parent="showFieldTypeDropdown"
      class="h-10 w-12 flex-none"
      menu-width="w-32"
    >
      <template #toggle-button>
        <div
          class="flex h-10 w-12 items-center justify-center space-x-1 rounded-l border"
          :class="[
            showFieldTypeDropdown
              ? 'z-10 border border-blue-600 bg-blue-50 ring-1 ring-blue-600'
              : 'border-r-0 border-gray-200 bg-gray-50',
            isCustomField ? 'cursor-pointer' : 'cursor-inherit',
            {
              'hover:border-r hover:border-gray-300':
                isCustomField && !showFieldTypeDropdown,
            },
          ]"
          @click="toggleDropdown"
        >
          <icon-base :icon="getIconForField(field)" />
          <span v-if="isCustomField" class="flex items-center text-gray-400">
            <icon-base
              class="transform transition-all"
              height="6"
              width="10"
              :icon="IconDropdownArrow"
              :class="{ 'rotate-180': showFieldTypeDropdown }"
            />
          </span>
        </div>
      </template>
      <template #listItem="{ option, optionKey }">
        <div
          class="flex cursor-pointer items-center space-x-1 p-1 px-2 hover:bg-gray-100"
          @click="
            handleFieldTypeChange(optionKey as CheckoutApplicationFieldType)
          "
        >
          <icon-base
            v-if="optionKey"
            :icon="
              fieldIcons[optionKey as CheckoutApplicationFieldType] ?? IconText
            "
          />
          <span>{{ option }}</span>
        </div>
      </template>
    </lf-dropdown>
    <div class="group relative w-full">
      <lf-input
        :name="field.id"
        :value="field.label"
        :disabled="!isCustomField"
        :has-padding="false"
        no-margin
        class="h-10"
        @key-released="fieldLocal.label = $event"
      />
      <div
        v-tooltip="$t('OFFER_CUSTOMISATION.REMOVE_FIELD')"
        class="absolute top-[12px] right-0 z-100 flex h-4 w-4 translate-x-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-400 opacity-0 transition-opacity group-hover:opacity-100"
        @click.prevent="removeField"
      >
        <icon-base
          :icon="IconX"
          height="10"
          width="10"
          viewBox="0 0 16 16"
          class="-mt-[2px] text-white"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRaw } from "vue";

import { useCheckoutOptionsQuery } from "@/hooks/queries/options";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import IconCurrency from "@/components/icons/offerTemplate/IconCurrency.vue";
import IconDate from "@/components/icons/offerTemplate/IconDate.vue";
import IconFloat from "@/components/icons/offerTemplate/IconFloat.vue";
import IconNumber from "@/components/icons/offerTemplate/IconNumber.vue";
import IconOption from "@/components/icons/offerTemplate/IconOption.vue";
import IconPercent from "@/components/icons/offerTemplate/IconPercent.vue";
import IconText from "@/components/icons/offerTemplate/IconText.vue";
import IconTextArea from "@/components/icons/offerTemplate/IconTextArea.vue";
import IconUrl from "@/components/icons/offerTemplate/IconUrl.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

import type { CheckoutDataField } from "@/models/checkout";
import type { CheckoutApplicationFieldType } from "@/models/options";
import type { Component } from "vue";

const unsupportedCustomFieldTypes = [
  "boolean",
  "currency",
  "date",
  "options",
  "sub_options",
  "addresses",
  "ssn_itin",
  "phone_numbers",
  "phone_number_with_additional_phone_numbers",
];

const fieldIcons: Record<string, Component> = {
  percentage: IconPercent,
  currency: IconCurrency,
  string: IconText,
  integer: IconNumber,
  date: IconDate,
  options: IconOption,
  sub_options: IconOption,
  url: IconUrl,
  float: IconFloat,
  text: IconTextArea,
  email: IconText,
  pii: IconText,
  boolean: IconOption,
};

const { field } = defineProps<{
  field: CheckoutDataField;
}>();

const emit = defineEmits<{
  deleteField: [];
}>();

const { data: options } = useCheckoutOptionsQuery();

const showFieldTypeDropdown = ref(false);
const fieldLocal = ref(field);

const isCustomField = computed(() => field.type === "custom_field");

const isApplicationField = computed(() => field.type === "application_field");

const customFieldTypes = computed(() => {
  const fieldTypes = toRaw(options.value?.definition.field_types);

  if (!fieldTypes) {
    return {};
  }

  for (const unsupportedCustomFieldType of unsupportedCustomFieldTypes) {
    delete fieldTypes[unsupportedCustomFieldType];
  }

  delete fieldTypes["sub_options"];

  return fieldTypes;
});

const getIconForField = (field: CheckoutDataField): Component => {
  const fallback = fieldIcons["text"];

  if (isCustomField.value && field.value_type) {
    return fieldIcons[field.value_type];
  }

  if (!isApplicationField.value) {
    return fallback;
  }

  const blocks = options.value?.definition.application_blocks;

  if (!blocks?.length) {
    return fallback;
  }

  for (const block of blocks) {
    if (block.id !== field.block) {
      continue;
    }

    for (const fieldDefinition of block.fields) {
      if (!fieldDefinition.field_type) {
        continue;
      }

      for (const fieldDefinition of block.fields) {
        if (!fieldDefinition.field_type) {
          continue;
        }

        if (fieldDefinition.id === field.name) {
          return fieldIcons[fieldDefinition.field_type] ?? fallback;
        }
      }
    }
  }

  return fallback;
};

const handleFieldTypeChange = (option: CheckoutApplicationFieldType) => {
  fieldLocal.value.value_type = option;
  showFieldTypeDropdown.value = false;
};

const removeField = () => {
  emit("deleteField");
  showFieldTypeDropdown.value = false;
};

const toggleDropdown = () => {
  if (!isCustomField.value) {
    return;
  }

  showFieldTypeDropdown.value = !showFieldTypeDropdown.value;
};
</script>
